import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Download, CheckCircle2, XCircle, Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useBulkUploadAssets } from '@/api/hooks/useAssets';

interface ImportResult {
  success: boolean;
  importedCount?: number;
  duplicateCount?: number;
  errorMessage?: string;
}

export default function AssetImportPage() {
  const { t } = useTranslation(['assets', 'common']);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const uploadMutation = useBulkUploadAssets();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setResult(null);
      }
    }
  };

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setSelectedFile(file);
        setResult(null);
      }
    };
    input.click();
  };

  const handleProcess = async () => {
    if (!selectedFile) return;

    try {
      const data = await uploadMutation.mutateAsync(selectedFile);
      setResult({
        success: true,
        importedCount: data.importedCount,
        duplicateCount: data.duplicateCount,
      });
    } catch (error) {
      setResult({
        success: false,
        errorMessage: error instanceof Error ? error.message : t('import.errorGeneric'),
      });
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'ASSET_NUMBER,DESCRIPTION,LOCATION,TAG_NUMBER,NOTES\nAST001,Example Asset,1,EPC001,Sample notes\nAST002,Another Asset,2,,No EPC assigned';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asset_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const isProcessing = uploadMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/assets">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('import.title')}</h1>
          <p className="text-muted-foreground">{t('import.description')}</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* CSV Format Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('import.csvFormat')}
            </CardTitle>
            <CardDescription>{t('import.csvFormatDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">{t('import.requiredColumns')}:</p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li><code className="bg-muted px-1 rounded">ASSET_NUMBER</code> - {t('import.columnAssetNumber')}</li>
                <li><code className="bg-muted px-1 rounded">DESCRIPTION</code> - {t('import.columnDescription')}</li>
                <li><code className="bg-muted px-1 rounded">LOCATION</code> - {t('import.columnLocation')}</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">{t('import.optionalColumns')}:</p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li><code className="bg-muted px-1 rounded">TAG_NUMBER</code> - {t('import.columnTagNumber')}</li>
                <li><code className="bg-muted px-1 rounded">NOTES</code> - {t('import.columnNotes')}</li>
              </ul>
            </div>
            <Button variant="outline" onClick={handleDownloadTemplate} className="w-full">
              <Download className="mr-2 h-4 w-4" />
              {t('import.downloadTemplate')}
            </Button>
          </CardContent>
        </Card>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {t('import.uploadFile')}
            </CardTitle>
            <CardDescription>{t('import.uploadDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drop Zone */}
            <div
              onClick={handleFileSelect}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer
                ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
                ${isProcessing ? 'pointer-events-none opacity-50' : ''}
              `}
            >
              <Upload className={`h-10 w-10 mb-3 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className="text-sm text-muted-foreground text-center">
                {t('import.dragDrop')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('import.acceptedFormat')}
              </p>
            </div>

            {/* Selected File */}
            {selectedFile && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <FileText className="h-8 w-8 text-primary" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setResult(null);
                  }}
                  disabled={isProcessing}
                >
                  {t('common:buttons.remove')}
                </Button>
              </div>
            )}

            {/* Process Button */}
            <Button
              onClick={handleProcess}
              disabled={!selectedFile || isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('import.processing')}
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  {t('import.processButton')}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {result.success ? (
            <>
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-600">{t('import.successTitle')}</AlertTitle>
                <AlertDescription className="text-green-600">
                  {t('import.successMessage', { count: result.importedCount })}
                </AlertDescription>
              </Alert>

              {(result.duplicateCount ?? 0) > 0 && (
                <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950/20">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertTitle className="text-blue-600">{t('import.infoTitle')}</AlertTitle>
                  <AlertDescription className="text-blue-600">
                    {t('import.duplicatesFound', { count: result.duplicateCount })}
                  </AlertDescription>
                </Alert>
              )}
            </>
          ) : (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>{t('import.errorTitle')}</AlertTitle>
              <AlertDescription>{result.errorMessage}</AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
